import { useEffect, useRef, useState } from 'react'
import Upload from '@mui/icons-material/Upload'
import { Stack, Typography } from '@mui/material'
import ExpandAndGrow from '../expand-and-grow/ExpandAndGrow'
import LabeledProgressIndicator from '../labeled-progress-indicator/LabeledProgressIndicator'
import OutlinedCard from '../outlined-card/OutlinedCard'
import { useFileUploadContext } from '../upload-files/FileUploadProvider'

export default function UploadingFilesCard() {
  const { numberToUpload, error } = useFileUploadContext()
  const [show, setShow] = useState(numberToUpload > 0)
  const [success, setSuccess] = useState(false)

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    // This handles when we start uploading files.
    // We want to expand the card when we start uploading.
    if (numberToUpload > 0 && !show) {
      setShow(true)
      setSuccess(false)
    }

    // This handles when we finish uploading all files.
    // We want to show the "success" state for a couple seconds, then collapse.
    // Unless there was an error registering the documents.
    if (numberToUpload === 0 && show) {
      if (!error) {
        setSuccess(true)
        timeoutRef.current = setTimeout(() => {
          setShow(false)
        }, 2000)
        return () => {
          if (timeoutRef.current) clearTimeout(timeoutRef.current)
        }
      } else {
        setShow(false)
      }
    }
  }, [error, numberToUpload, show])

  // If the user loads in more files while we're showing the success state,
  // we want to reset back to the uploading state.
  useEffect(() => {
    if (success && show && numberToUpload > 0) {
      setSuccess(false)
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [numberToUpload, show, success])

  return (
    <ExpandAndGrow show={show} expandedHeight={40}>
      <OutlinedCard color={success ? 'success.main' : undefined}>
        {!success && (
          <LabeledProgressIndicator>
            {`${numberToUpload} file${
              numberToUpload > 1 ? 's' : ''
            } uploading...`}
          </LabeledProgressIndicator>
        )}
        {success && (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Upload fontSize="small" color="success" />
            <Typography variant="body2" color="success.main">
              Files uploaded successfully!
            </Typography>
          </Stack>
        )}
      </OutlinedCard>
    </ExpandAndGrow>
  )
}
